import addonsApi, { useGetAddonsDataQuery } from '@/src/api/rtkQuery/addonsApi'
import { useGetItineraryDataQuery } from '@/src/api/rtkQuery/itineraryApi'
import useFormatCurrencyForItinerary from '@/src/hooks/currency/useFormatCurrencyForItinerary'
import useGetItineraryId from '@/src/hooks/itinerary/useGetItineraryId'
import { useAppDispatch } from '@/src/redux/store'
import { AncillaryBaggageInfo, SelectedAncillaryType } from '@/src/types/itinerary'
import { useCallback, useState } from 'react'

const useBaggageProcessor = () => {
    const itineraryId = useGetItineraryId()
    const { formatCurrency } = useFormatCurrencyForItinerary()
    const { data: addonsData } = useGetAddonsDataQuery({ itineraryId })
    const dispatch = useAppDispatch()

    const { paxCount } = useGetItineraryDataQuery(
        { itineraryId },
        {
            selectFromResult: ({ data }) => {
                try {
                    if (data) {
                        return {
                            paxCount: data.searchCriteria.paxInfo.adults + data.searchCriteria.paxInfo.children
                        }
                    }
                    return {
                        paxCount: 0
                    }
                } catch (e) {
                    return {
                        paxCount: 0
                    }
                }
            }
        }
    )

    const [selectedSegmentIndex, setSelectedSegmentIndex] = useState<number>(0)

    const { journeyAncillaries, selectedAncillaries = [] } = addonsData || {}
    const { baggageAncillaries } = journeyAncillaries || {}
    const { availableSegments = [], segmentWiseAncillaryData } = baggageAncillaries || {}

    const selectedSegment = availableSegments?.[selectedSegmentIndex] || { from: '', to: '', key: '' }
    const baggageOptions = segmentWiseAncillaryData?.[selectedSegment.key] || []
    const selectedBaggageOptions =
        selectedAncillaries.find(
            ancillary =>
                ancillary.segmentOriginDestinationInfo.from === selectedSegment.from &&
                ancillary.segmentOriginDestinationInfo.to === selectedSegment.to
        )?.baggageDetails || []

    const handleAddBaggage = useCallback(
        (baggage: AncillaryBaggageInfo) => {
            dispatch(
                addonsApi.util.updateQueryData('getAddonsData', { itineraryId }, draft => {
                    if (!draft.selectedAncillaries) {
                        draft.selectedAncillaries = []
                    }

                    const selectedAncillarySegmentIndex = draft.selectedAncillaries.findIndex(
                        ancillary =>
                            ancillary.segmentOriginDestinationInfo.from === selectedSegment.from &&
                            ancillary.segmentOriginDestinationInfo.to === selectedSegment.to
                    )

                    if (selectedAncillarySegmentIndex !== -1) {
                        // Update existing segment
                        draft.selectedAncillaries[selectedAncillarySegmentIndex].baggageDetails.push(baggage)
                    } else {
                        // Add new segment
                        draft.selectedAncillaries.push({
                            segmentOriginDestinationInfo: {
                                from: selectedSegment.from,
                                to: selectedSegment.to
                            },
                            mealDetails: [],
                            baggageDetails: [baggage],
                            seatDetails: []
                        } as SelectedAncillaryType)
                    }
                })
            )
        },
        [itineraryId, selectedSegment]
    )

    const handleRemoveBaggage = useCallback(
        (baggage: AncillaryBaggageInfo) => {
            dispatch(
                addonsApi.util.updateQueryData('getAddonsData', { itineraryId }, draft => {
                    if (!draft.selectedAncillaries) return

                    const selectedAncillarySegmentIndex = draft.selectedAncillaries.findIndex(
                        ancillary =>
                            ancillary.segmentOriginDestinationInfo.from === selectedSegment.from &&
                            ancillary.segmentOriginDestinationInfo.to === selectedSegment.to
                    )

                    if (selectedAncillarySegmentIndex !== -1) {
                        // Find the first occurrence of the meal to remove
                        const baggageIndex = draft.selectedAncillaries[
                            selectedAncillarySegmentIndex
                        ].baggageDetails.findIndex(baggageDetail => baggageDetail.baggageCode === baggage.baggageCode)

                        // Remove only the first occurrence if found
                        if (baggageIndex !== -1) {
                            draft.selectedAncillaries[selectedAncillarySegmentIndex].baggageDetails.splice(
                                baggageIndex,
                                1
                            )
                        }
                    }
                })
            )
        },
        [itineraryId, selectedSegment]
    )

    return {
        selectedSegment,
        availableSegments,
        selectedSegmentIndex,
        baggageOptions,
        paxCount,
        selectedBaggageOptions,
        setSelectedSegmentIndex,
        handleAddBaggage,
        handleRemoveBaggage,
        formatCurrency
    }
}

export default useBaggageProcessor
